<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { onMount } from 'svelte';
  import { AppState, SetNotification } from '../../../etc/stores';
  import { formatMoney } from '../../../etc/utils';
  import { Auth } from '../../../services/auth';
  import { Container } from '../../../services/container';
  import { SessionManager } from '../../../services/session-manager';
  import Analytics from '../../../services/analytics';
  import Button from '../../form/Button.svelte';
  import Text from '../../misc/Text.svelte';
  import Title from '../../misc/Title.svelte';
  import Link from '../../misc/Link.svelte';
  import Close from '../../icons/Close.svelte';
  import AnimatedConfetti from '../../misc/AnimatedConfetti.svelte';
  import AnimatedCoin from '../../misc/AnimatedCoin.svelte';
  import PoweredBy from '../../misc/PoweredBy.svelte';
  import { replaceVariables } from '../../../i18n';
  import { Checkout } from '../../../services/checkout';
  import AuthConnector from '../../misc/AuthConnector.svelte';
  import sessionKeys from '../../../etc/sessionKeys';

  const auth = Container.get(Auth);
  const session = Container.get(SessionManager) as SessionManager;
  const checkout = Container.get(Checkout) as Checkout;

  let setLoading;
  let handleConnectCustomer;

  $: showLoginButton = $AppState.auth?.providerData || auth.canOpenLoginModal();

  const t = (text: string, values?: any) => {
    return replaceVariables($_(`messages.got_coins.${text}`), values);
  };

  const getTitle = () => {
    const { contactFirstName } = $AppState.checkout;
    const { name } = $AppState.customer;
    const NAME = name || contactFirstName;
    const id = NAME ? 'with_name' : 'default';

    return t(`title.${id}`, { NAME });
  };

  const getExplanation = () => {
    const EMAIL =
      $AppState.customer.mail ||
      $AppState.checkout.contactEmail ||
      $AppState.auth.providerData.email;
    return t('explanation', { EMAIL });
  };

  const getBtnLabel = () => {
    const id = $AppState.auth.connected ? 'authenticated' : 'unauthenticated';

    return t(`btn.${id}`);
  };

  const onAfterConnectCustomer = async next => {
    const nextDash = {
      route: '/dashboard'
    };

    onGAEventGetCoins();

    await checkout.verifyProvisionalCheckoutWix();

    session.set(sessionKeys.COLLECT_COINS, true);

    AppState.update(state => ({
      ...state,
      website: {
        ...state.website,
        popoverOpen: true,
        popoverRouteOnOpen: $AppState.auth.connected ? nextDash : next
      }
    }));
    closeMessage();

    return { skipNavigate: true };
  };

  const onGAEventClickGetCoins = event => {
    Analytics.logEvent(event, `rewards-${Analytics.getCategoryLoginState()}`);
  };

  const onGAEventGetCoins = () => {
    Analytics.logEvent(
      'click-collect-coins-success',
      `rewards-${Analytics.getCategoryLoginState()}`
    );
  };

  async function handleSubmit() {
    if ($AppState.auth.connected) {
      AppState.update(state => ({
        ...state,
        website: {
          ...state.website,
          popoverOpen: true,
          checkout: false,
          lightIcon: false,
          connectingAccount: false,
          popoverRouteOnOpen: {
            route: '/dashboard'
          }
        }
      }));
      closeMessage();
      return;
    }
    if (await auth.isAuthenticatedOnWebsite()) {
      onGAEventClickGetCoins('click-collect-coins-logged');
      await handleConnectCustomer();
    } else if (auth.canOpenLoginModal()) {
      onGAEventClickGetCoins('click-collect-coins-guest');
      auth.openLoginModal();
    }
  }

  const triggerConnectCustomer = () => {
    handleConnectCustomer();
  };

  const closeMessage = () => {
    SetNotification('');
  };

  const goToHowItWorks = () => {
    Analytics.logEvent(
      'click-how-it-works',
      `rewards-${Analytics.getCategoryLoginState()}`
    );

    AppState.update(state => ({
      ...state,
      website: {
        ...state.website,
        popoverOpen: true,
        popoverRouteOnOpen: {
          route: '/how-it-works',
          navigationOptions: {
            state: {
              onBackButton: () => {
                AppState.update(state => ({
                  ...state,
                  website: {
                    ...state.website,
                    popoverOpen: false
                  }
                }));
              }
            }
          }
        }
      }
    }));
  };

  onMount(() => {
    sessionStorage.removeItem('@twism:purchase');
  });
</script>

<AuthConnector
  {triggerConnectCustomer}
  {onAfterConnectCustomer}
  loggerContext="YouGotCoins.svelte"
  bind:handleConnectCustomer
  bind:setLoading
  let:loading
>
  <div class="twism-popup-gotcoins--container {auth.provider}">
    <div class="twism-popup-gotcoins--header">
      <div class="twism-popup-gotcoins--close" on:click={closeMessage}>
        <Close />
      </div>
      <div class="twism-popup-gotcoins--coin">
        <AnimatedCoin image={$AppState.business.logo.publicPath.large} />
        <AnimatedConfetti />
      </div>
    </div>
    <div class="twism-popup-gotcoins--wrapper">
      <Title fontSize="18px" fontWeight="700" color="#012A5A">
        {getTitle()}
      </Title>
      <div class="twism-popup-gotcoins--description">
        <Text fontSize="16px" lineHeight="22px" color="#012A5A">
          {t('description_subtext')}
        </Text>
        <Title fontSize="36px" lineHeight="50px" color="#012A5A">
          {t('description', {
            AMOUNT: formatMoney(
              $AppState.business.location,
              $AppState.website.coins || 0
            )
          })}
        </Title>
      </div>
      {#if !$AppState.auth.connected}
        <div class="twism-popup-gotcoins--explanation">
          <Text fontSize="16px" lineHeight="22px" color="#597492">
            {@html getExplanation()}
          </Text>
        </div>
      {/if}
      {#if showLoginButton}
        <div class="twism-popup-gotcoins--button">
          <span>🎉</span>
          <Button label={getBtnLabel()} on:click={handleSubmit} {loading} />
        </div>
      {:else}
        <div class="twism-popup-gotcoins--guest">
          <Text fontSize="14px" lineHeight="16px" color="#012A5A">
            {@html t('guest')}
          </Text>
        </div>
      {/if}
      {#if !$AppState.auth.connected}
        <div class="twism-popup-gotcoins--link">
          <Link fontSize="14px" on:click={goToHowItWorks}>
            {t('how_it_works')}
          </Link>
        </div>
      {/if}
      <div class="twism-popup-gotcoins--bottom">
        <div class="twism-popup-gotcoins--footer">
          <PoweredBy />
        </div>
      </div>
    </div>
  </div>
</AuthConnector>

<style lang="scss">
  @use './styles.scss' as *;

  .twism-popup-gotcoins {
    &--container {
      background-color: #ffffff;
      background-image: var(--twism-theme-slightGradient);
      background-size: 100% 350px;
      background-repeat: no-repeat;
      box-shadow: 0px 16.8309px 57.7059px -15.6287px rgba(4, 33, 67, 0.26);
      border-radius: 20px;
      width: 360px;
      height: auto;
      display: flex;
      flex-flow: column nowrap;
      align-items: center;
      box-sizing: border-box;
      padding-bottom: 50px;
      transform-origin: inherit;
      position: relative;

      @media screen and (max-width: 468px) {
        max-height: calc(100vh - 110px);
        width: 100%;
      }
    }

    &--wrapper {
      display: flex;
      flex-flow: column nowrap;
      align-items: center;
      padding: 0 24px;
      width: calc(100% - 48px);
    }

    &--close {
      position: absolute;
      top: 24px;
      right: 24px;
      width: 30px;
      height: 30px;
      cursor: pointer;
      border-radius: 8px;
      display: flex;
      justify-content: center;
      align-items: center;
      background: transparent;
      border: 1px solid var(--twism-theme-illustration-quinary);
      transition: background 0.2s ease-in-out;
      z-index: 5;

      :global(svg path) {
        fill: var(--twism-theme-illustration-quinary);
      }

      @media screen and (min-width: 768px) {
        &:hover {
          background: rgba(0, 0, 0, 0.04);
        }
      }
    }

    &--header {
      height: 140px;
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: flex-end;
      background-position: top;
      background-repeat: no-repeat;
      background-size: 100% 95px;
      margin-bottom: 24px;

      @media screen and (max-width: 468px) {
        margin-bottom: 16px;
      }
    }

    &--coin {
      width: 96px;
      height: 96px;
      display: flex;
      justify-content: center;
      box-sizing: border-box;
      display: flex;
      justify-content: center;
      align-items: center;
      padding-bottom: 6px;
      padding-left: 2px;
      background-position: center;
      background-repeat: no-repeat;
      background-size: contain;

      @media screen and (max-width: 468px) {
        width: 88px;
        height: 88px;
      }

      &--logo {
        border-radius: 50%;
        overflow: hidden;
        width: 72px;
        height: 72px;

        @media screen and (max-width: 468px) {
          width: 66px;
          height: 66px;
          transform: translate(-0.5px);
        }
      }
    }

    &--description {
      margin-top: 32px;
      margin-bottom: 30px;

      @media screen and (max-width: 468px) {
        margin-top: 20px;
        margin-bottom: 20px;

        :global(h1) {
          font-size: 32px !important;
          line-height: 45px !important;
        }
      }
    }

    &--explanation {
      margin-bottom: 24px;

      @media screen and (max-width: 468px) {
        :global(p) {
          font-size: 14px !important;
          line-height: 20px !important;
        }
      }
    }

    &--guest {
      max-width: 215px;
    }

    &--button {
      width: 100%;
      position: relative;

      span {
        font-size: 40px;
        position: absolute;
        bottom: 24px;
        left: 16px;
        z-index: 10;
        pointer-events: none;

        @media screen and (max-width: 468px) {
          font-size: 36px;
        }
      }
    }

    &--bottom {
      position: absolute;
      bottom: 14px;
    }

    &--link {
      margin-top: 24px;
    }

    &--footer {
      height: 16px;
    }
  }
</style>
